"use client"

import React, { useState, useEffect } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import StickySaveBar from "@/shared/components/sticky-save-bar"
import { DirtyFieldsProvider } from "@/shared/context/dirty-fields-context"
import { 
    createHomeownerProfileFormSchema,
    HomeownerProfileFormType 
} from "../../schemas/homeowner-profile-form.schema"
import { HomeWithHomeOwnerFormType } from "@/features/homeowners/schema/home-with-home-owner.schema"
import errorToast from "../../../../shared/lib/toast/error.toast"
import successToast from "../../../../shared/lib/toast/success.toast"
import { updateHomeownerProfile } from "@/features/profile/api/homeowner-profile.api"
import { getDirtyFieldsValues } from "../../../../shared/lib/utils"
import FormTextInput from "@/shared/components/form-inputs/form-text-input"
import FormPhoneInput from "@/shared/components/form-inputs/form-phone-input"
import FormYesNoInput from "@/shared/components/form-inputs/form-yes-no-input"
import FormIntegerInput from "@/shared/components/form-inputs/form-integer-input"
import FormSelectInput from "@/shared/components/form-inputs/form-select-input"
import FormTextareaInput from "@/shared/components/form-inputs/form-textarea-input"
import FormRadioInput from "@/shared/components/form-inputs/form-radio-input"
import FormDateInput from "@/shared/components/form-inputs/form-date-input"
import { yesNoOptions, yesNoRejectedOptions } from "@/shared/constants/options"
import { residentTypeOptions, residenceTypeOptions } from "@/features/homeowners/constants/options"
import HomeDetailsWrapper from "@/features/homeowners/components/home-details-wrapper"
import { Profile } from "@/features/auth/types/auth-actions.type"
import { getHomeownerProfile } from "@/features/profile/api/homeowner-profile.api"
import { useSession } from "@/features/auth/context/session.context"

interface HomeownerProfileFormProps {
    profile: Profile
    currentStep?: number
}

function HomeownerProfileForm({ profile, currentStep }: HomeownerProfileFormProps) {
    const { refreshSession } = useSession()
    const [showSuccessBar, setShowSuccessBar] = useState(false)
    const [isFormInitialized, setIsFormInitialized] = useState(false)
    const [isLoading, setIsLoading] = useState(true)
    const [homeownerData, setHomeownerData] = useState<any>(null)

    // Initialize form with dynamic schema based on currentStep
    const form = useForm<HomeWithHomeOwnerFormType>({
        resolver: zodResolver(createHomeownerProfileFormSchema(currentStep) as any),
        // Use default mode (onSubmit) like volunteer form - validation will be triggered manually
        // This prevents errors from showing on initial load
        defaultValues: {
            homeOwner: {
                name: profile.fullname || "",
                email: profile.email || "",
                phone: profile.phone || "",
                primaryLanguage: "",
                secondaryContact: "",
                secondaryPhone: "",
                dateOfBirth: undefined,
                residentType: undefined,
                medicalNeeds: "",
                numChildrenUnder18: undefined,
                isFirstResponder: "",
                isSingleParent: "",
                isVeteran: "",
                appliedFEMA: "",
                femaStatus: null,
                appliedSBA: "",
                sbaStatus: null,
                safetyConcerns: "",
                prayerRequest: "",
                ownerStory: "",
            },
            home: {
                address: "",
                city: "",
                state: "",
                zipCode: "",
                longitude: undefined,
                latitude: undefined,
                county: "",
                dogsInHome: "",
                shedNeeded: "",
                hasFloodInsurance: "",
                hasHomeInsurance: null,
                googlePicLink: "",
                projectGeneralNotes: "",
                isReported: true,
            },
        },
    })

    // Fetch homeowner data on mount
    useEffect(() => {
        const fetchHomeownerData = async () => {
            try {
                setIsLoading(true)
                const result = await getHomeownerProfile()
                
                if (result.success && result.data) {
                    setHomeownerData(result.data)
                    
                    // Map homeowner and home data to form structure
                    const homeowner = result.data
                    const home = homeowner.home || {}
                    
                    // Convert dateOfBirth from backend (string or Date) to Date object for form.
                    // dateOfBirth is stored on the user record, not the homeowner record.
                    let dateOfBirthDate: Date | undefined = undefined
                    const rawDateOfBirth = homeowner.user?.dateOfBirth ?? homeowner.dateOfBirth
                    if (rawDateOfBirth) {
                        if (rawDateOfBirth instanceof Date) {
                            dateOfBirthDate = new Date(rawDateOfBirth.getFullYear(), rawDateOfBirth.getMonth(), rawDateOfBirth.getDate())
                        } else if (typeof rawDateOfBirth === 'string') {
                            const dateStr = rawDateOfBirth.split('T')[0] // Get just the date part if ISO string
                            const [year, month, day] = dateStr.split('-').map(Number)
                            if (year && month && day) {
                                dateOfBirthDate = new Date(year, month - 1, day)
                            }
                        }
                    }
                    
                    const formData = {
                        homeOwner: {
                            name: homeowner.user?.fullname || profile.fullname || "",
                            email: homeowner.user?.email || profile.email || "",
                            phone: homeowner.user?.phone || profile.phone || "",
                            primaryLanguage: homeowner.primaryLanguage || "",
                            secondaryContact: homeowner.secondaryContact || "",
                            secondaryPhone: homeowner.secondaryPhone || "",
                            dateOfBirth: dateOfBirthDate,
                            residentType: homeowner.residentType || "",
                            medicalNeeds: homeowner.medicalNeeds || "",
                            numChildrenUnder18: homeowner.numChildrenUnder18,
                            isFirstResponder: homeowner.isFirstResponder || "",
                            isSingleParent: homeowner.isSingleParent || "",
                            isVeteran: homeowner.isVeteran || "",
                            appliedFEMA: homeowner.appliedFEMA || "",
                            femaStatus: homeowner.femaStatus || null,
                            appliedSBA: homeowner.appliedSBA || "",
                            sbaStatus: homeowner.sbaStatus || null,
                            safetyConcerns: homeowner.safetyConcerns || "",
                            prayerRequest: homeowner.prayerRequest || "",
                            ownerStory: homeowner.ownerStory || "",
                        },
                        home: {
                            address: home.address || "",
                            city: home.city || "",
                            state: home.state || "",
                            zipCode: home.zipCode || "",
                            longitude: home.longitude ? Number(home.longitude) : undefined,
                            latitude: home.latitude ? Number(home.latitude) : undefined,
                            county: home.county || "",
                            dogsInHome: home.dogsInHome || "",
                            shedNeeded: home.shedNeeded || "",
                            hasFloodInsurance: home.hasFloodInsurance || "",
                            hasHomeInsurance: home.hasHomeInsurance || null,
                            googlePicLink: home.googlePicLink || "",
                            projectGeneralNotes: home.projectGeneralNotes || "",
                            isReported: home.isReported ?? true,
                            repairabilityInfo: home.repairabilityInfo || {},
                            treeDebrisInfo: home.treeDebrisInfo || {},
                            roofingInfo: home.roofingInfo || {},
                            muckGutInfo: home.muckGutInfo || {},
                            moldRemediationInfo: home.moldRemediationInfo || {},
                            rebuildPrepInfo: home.rebuildPrepInfo || {},
                            roughCarpentryInfo: home.roughCarpentryInfo || {},
                            electricalInfo: home.electricalInfo || {},
                            hvacInfo: home.hvacInfo || {},
                            plumbingInfo: home.plumbingInfo || {},
                            bathroomRebuildInfo: home.bathroomRebuildInfo || {},
                            crawlSpaceInfo: home.crawlSpaceInfo || {},
                            insulationInstallInfo: home.insulationInstallInfo || {},
                            drywallHangingInfo: home.drywallHangingInfo || {},
                            mudAndTapeInfo: home.mudAndTapeInfo || {},
                            cabinetInstallInfo: home.cabinetInstallInfo || {},
                            interiorPaintInfo: home.interiorPaintInfo || {},
                            installInteriorDoorsInfo: home.installInteriorDoorsInfo || {},
                            trimInstallInfo: home.trimInstallInfo || {},
                            flooringInstallInfo: home.flooringInstallInfo || {},
                            exteriorDoorInstallInfo: home.exteriorDoorInstallInfo || {},
                            miscellaneousInfo: home.miscellaneousInfo || {},
                            homeStatusInfo: home.homeStatusInfo || {},
                            permitInspectionStatusInfo: home.permitInspectionStatusInfo || {},
                        },
                    }
                    
                    // Reset form with proper options to ensure it starts clean
                    form.reset(formData, {
                        keepDefaultValues: false,
                        keepDirty: false,
                        keepErrors: false,
                        keepIsSubmitted: false,
                        keepTouched: false,
                        keepIsValid: false,
                        keepSubmitCount: false,
                    })
                    
                    // Wait for form state to update before marking as initialized
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            const currentIsDirty = form.formState.isDirty
                            if (!currentIsDirty) {
                                setIsFormInitialized(true)
                            } else {
                                // If still dirty, try one more reset
                                const latestValues = form.getValues()
                                form.reset(latestValues, {
                                    keepDefaultValues: false,
                                    keepDirty: false,
                                })
                                setTimeout(() => {
                                    setIsFormInitialized(true)
                                }, 100)
                            }
                        })
                    })
                } else {
                    // If no homeowner data found, initialize with basic profile data
                    const formData = {
                        homeOwner: {
                            name: profile.fullname || "",
                            email: profile.email || "",
                            phone: profile.phone || "",
                        },
                        home: {},
                    }
                    form.reset(formData, {
                        keepDefaultValues: false,
                        keepDirty: false,
                        keepErrors: false,
                        keepIsSubmitted: false,
                        keepTouched: false,
                        keepIsValid: false,
                        keepSubmitCount: false,
                    })
                    requestAnimationFrame(() => {
                        requestAnimationFrame(() => {
                            setIsFormInitialized(true)
                        })
                    })
                }
            } catch (error) {
                console.error("[HomeownerProfileForm] Error fetching homeowner data:", error)
                // Initialize with basic profile data on error
                const formData = {
                    homeOwner: {
                        name: profile.fullname || "",
                        email: profile.email || "",
                        phone: profile.phone || "",
                    },
                    home: {},
                }
                form.reset(formData, {
                    keepDefaultValues: false,
                    keepDirty: false,
                    keepErrors: false,
                    keepIsSubmitted: false,
                    keepTouched: false,
                    keepIsValid: false,
                    keepSubmitCount: false,
                })
                requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                        setIsFormInitialized(true)
                    })
                })
            } finally {
                setIsLoading(false)
            }
        }

        if (!isFormInitialized) {
            fetchHomeownerData()
        }
    }, [profile, form, isFormInitialized])

    // Watch for form changes to show sticky save bar
    const { dirtyFields, isDirty, isSubmitting } = form.formState

    // DEBUG: Log form state changes
    useEffect(() => {
        console.log('[HomeownerProfileForm] Form state changed:', {
            isDirty,
            isFormInitialized,
            dirtyFieldsKeys: Object.keys(dirtyFields),
            dirtyFields,
            isSubmitting,
            showSuccessBar,
        })
    }, [isDirty, isFormInitialized, dirtyFields, isSubmitting, showSuccessBar])

    useEffect(() => {
        if (isDirty && showSuccessBar) {
            setShowSuccessBar(false)
        }
    }, [isDirty, showSuccessBar])

    const onSubmit = async () => {
        // Validate form before submission, especially if currentStep === 1
        const isValid = await form.trigger()
        console.log('[HomeownerProfileForm] onSubmit - Validation result:', {
            isValid,
            errors: form.formState.errors,
            errorCount: Object.keys(form.formState.errors).length,
        })
        
        if (!isValid) {
            // Find and scroll to first error using React Hook Form's setFocus
            const findFirstError = (errors: any, path = ''): string | null => {
                for (const key in errors) {
                    const currentPath = path ? `${path}.${key}` : key
                    if (errors[key]?.message) {
                        return currentPath
                    }
                    if (typeof errors[key] === 'object' && errors[key] !== null && !errors[key].message) {
                        const nested = findFirstError(errors[key], currentPath)
                        if (nested) return nested
                    }
                }
                return null
            }
            
            const firstErrorPath = findFirstError(form.formState.errors)
            if (firstErrorPath) {
                // Use React Hook Form's setFocus which works with nested paths
                setTimeout(() => {
                    form.setFocus(firstErrorPath as any, { shouldSelect: false })
                }, 100)
            }
            return
        }
        
        console.log('[HomeownerProfileForm] onSubmit called', {
            formDirtyFields: form.formState.dirtyFields,
            isDirty: form.formState.isDirty,
        })
        
        const data = form.getValues()
        
        try {
            // Build dirty fields object manually to handle nested paths
            const dirtyFieldsObj: Partial<HomeWithHomeOwnerFormType> = {}
            
            // Check if homeOwner has any dirty fields
            if (form.formState.dirtyFields.homeOwner) {
                console.log('[HomeownerProfileForm] homeOwner is dirty:', form.formState.dirtyFields.homeOwner)
                dirtyFieldsObj.homeOwner = getDirtyFieldsValues(
                    form.formState.dirtyFields.homeOwner as any,
                    data.homeOwner
                ) as any
                console.log('[HomeownerProfileForm] Extracted homeOwner dirty fields:', dirtyFieldsObj.homeOwner)
            }
            
            // Check if home has any dirty fields
            if (form.formState.dirtyFields.home) {
                console.log('[HomeownerProfileForm] home is dirty:', form.formState.dirtyFields.home)
                dirtyFieldsObj.home = getDirtyFieldsValues(
                    form.formState.dirtyFields.home as any,
                    data.home
                ) as any
                console.log('[HomeownerProfileForm] Extracted home dirty fields:', dirtyFieldsObj.home)
            }
            
            console.log('[HomeownerProfileForm] onSubmit - Data being sent:', {
                fullData: data,
                dirtyFields: dirtyFieldsObj,
                dirtyFieldsKeys: Object.keys(dirtyFieldsObj),
                homeOwnerDirty: !!form.formState.dirtyFields.homeOwner,
                homeDirty: !!form.formState.dirtyFields.home,
                homeOwnerKeys: dirtyFieldsObj.homeOwner ? Object.keys(dirtyFieldsObj.homeOwner) : [],
                homeKeys: dirtyFieldsObj.home ? Object.keys(dirtyFieldsObj.home) : [],
            })
            
            if (Object.keys(dirtyFieldsObj).length === 0) {
                console.warn('[HomeownerProfileForm] No dirty fields detected, skipping save')
                return
            }
            
            console.log('[HomeownerProfileForm] Calling updateHomeownerProfile...')
            // Pass homeowner ID if available to avoid searching again
            const homeownerId = homeownerData?.id
            const result = await updateHomeownerProfile(dirtyFieldsObj, homeownerId)
            
            if (!result) {
                errorToast("An error occurred while updating your profile")
                return
            }
            
            console.log('[HomeownerProfileForm] onSubmit - Response:', {
                success: result.success,
                message: result.success === false ? result.message : undefined,
                data: result.success ? result.data : undefined,
            })
            
            if (!result.success) {
                errorToast(result.message || "Failed to update profile")
                return
            }
            
            successToast("Profile updated successfully")
            
            // Mark form as clean by resetting to current values (which are now saved)
            const currentValues = form.getValues()
            form.reset(currentValues, {
                keepDefaultValues: false, // Update defaults to current values
                keepDirty: false,
                keepErrors: false,
                keepIsSubmitted: false,
                keepTouched: false,
                keepIsValid: false,
                keepSubmitCount: false,
            })
            
            // Show visual feedback that form was saved
            setShowSuccessBar(true)

            await refreshSession()
        } catch (error) {
            console.error("[HomeownerProfileForm] Error updating profile:", error)
            errorToast("An error occurred while updating your profile")
        }
    }

    // Handler for sticky bar save button
    const handleStickySave = async () => {
        console.log('[HomeownerProfileForm] handleStickySave called', {
            isFormInitialized,
            isDirty,
            dirtyFields,
            currentStep,
        })
        
        // Validate form before submission, especially if currentStep === 1
        const isValid = await form.trigger()
        
        console.log('[HomeownerProfileForm] handleStickySave - Validation result:', {
            isValid,
            errors: form.formState.errors,
            errorCount: Object.keys(form.formState.errors).length,
        })
        
        if (!isValid) {
            // Validation errors will be shown automatically by react-hook-form
            // Find the first error field (including nested paths) and scroll to it
            const findFirstError = (errors: any, path = ''): string | null => {
                for (const key in errors) {
                    const currentPath = path ? `${path}.${key}` : key
                    if (errors[key]?.message) {
                        console.log('[HomeownerProfileForm] Found error at:', currentPath, errors[key])
                        return currentPath
                    }
                    if (typeof errors[key] === 'object' && errors[key] !== null && !errors[key].message) {
                        const nested = findFirstError(errors[key], currentPath)
                        if (nested) return nested
                    }
                }
                return null
            }
            
            const firstErrorPath = findFirstError(form.formState.errors)
            console.log('[HomeownerProfileForm] First error path:', firstErrorPath)
            if (firstErrorPath) {
                // Use React Hook Form's setFocus which works with nested paths
                setTimeout(() => {
                    form.setFocus(firstErrorPath as any, { shouldSelect: false })
                }, 100)
            }
            return
        }
        
        console.log('[HomeownerProfileForm] Validation passed, calling onSubmit')
        // Just get the current form data and submit it
        // The onSubmit function already handles validation and dirty field checking
        await onSubmit()
    }

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
        )
    }

    // Calculate if form is clean (not dirty and initialized)
    const isFormClean = !isDirty && isFormInitialized
    const showRequiredCues = currentStep === 1

    return (
        <FormProvider {...form}>
            <DirtyFieldsProvider isFormClean={isFormClean}>
                <form onSubmit={form.handleSubmit(onSubmit)} noValidate className="space-y-5">
                {/* Homeowner Information Section */}
                <HomeDetailsWrapper heading="Homeowner Details">
                    <FormTextInput
                        inputGroupProps={{
                            label: "Homeowner Full Name"
                        }}
                        placeholder="Homeowner Name"
                        name="homeOwner.name"
                        control={form.control}
                    />
                    
                    <FormTextInput
                        inputGroupProps={{
                            label: "Email Address"
                        }}
                        placeholder="Email Address"
                        name="homeOwner.email"
                        type="email"
                        control={form.control}
                    />

                    <FormPhoneInput
                        inputGroupProps={{
                            label: "Phone Number"
                        }}
                        placeholder="Phone Number"
                        name="homeOwner.phone"
                        control={form.control}
                    />

                    <FormSelectInput
                        inputGroupProps={{
                            label: "Resident Type"
                        }}
                        options={residentTypeOptions}
                        name="homeOwner.residentType"
                        control={form.control}
                    />

                    <FormTextInput
                        inputGroupProps={{
                            label: "Primary Language"
                        }}
                        placeholder="Primary Language"
                        name="homeOwner.primaryLanguage"
                        control={form.control}
                    />

                    <FormTextInput
                        inputGroupProps={{
                            label: "Secondary Contact"
                        }}
                        placeholder="Secondary Contact"
                        name="homeOwner.secondaryContact"
                        control={form.control}
                    />

                    <FormPhoneInput
                        inputGroupProps={{
                            label: "Secondary Phone Number"
                        }}
                        placeholder="Secondary Phone Number"
                        name="homeOwner.secondaryPhone"
                        control={form.control}
                    />

                    <FormDateInput
                        inputGroupProps={{
                            label: showRequiredCues
                                ? "Birthdate of oldest resident in the home*"
                                : "Birthdate of oldest resident in the home",
                            highlightWhenEmpty: showRequiredCues,
                        }}
                        placeholder="Select birthdate of oldest resident in the home"
                        name="homeOwner.dateOfBirth"
                        control={form.control}
                        enableYearMonthDropdowns={true}
                        fromYear={new Date().getFullYear() - 100}
                        toYear={new Date().getFullYear()}
                    />

                    <FormTextInput
                        inputGroupProps={{
                            label: "Special (Medical) Needs"
                        }}
                        placeholder="Special (Medical) Needs"
                        name="homeOwner.medicalNeeds"
                        control={form.control}
                    />

                    <FormYesNoInput
                        inputGroupProps={{
                            label: showRequiredCues
                                ? "Is the resident a single parent or grandparent(s) raising children?*"
                                : "Is the resident a single parent or grandparent(s) raising children?",
                            highlightWhenEmpty: showRequiredCues,
                        }}
                        name="homeOwner.isSingleParent"
                        control={form.control}
                    />

                    <FormIntegerInput
                        inputGroupProps={{
                            label: "Number of children under 18 yo in home"
                        }}
                        placeholder="Number of children under 18 yo in home"
                        name="homeOwner.numChildrenUnder18"
                        control={form.control}
                    />

                    <FormYesNoInput
                        name="homeOwner.isFirstResponder"
                        inputGroupProps={{
                            label: showRequiredCues
                                ? "Is there a First Responder living in the home?*"
                                : "Is there a First Responder living in the home?",
                            highlightWhenEmpty: showRequiredCues,
                        }}
                        control={form.control}
                    />

                    <FormYesNoInput
                        name="homeOwner.isVeteran"
                        inputGroupProps={{
                            label: showRequiredCues
                                ? "Is there a Veteran living in the home?*"
                                : "Is there a Veteran living in the home?",
                            highlightWhenEmpty: showRequiredCues,
                        }}
                        control={form.control}
                    />

                    <FormRadioInput
                        inputGroupProps={{
                            label: "Does homeowner have Flood Insurance?"
                        }}
                        options={yesNoOptions}
                        name="home.hasFloodInsurance"
                        control={form.control}
                    />

                    <FormRadioInput
                        inputGroupProps={{
                            label: "Does Homeowner have Homeowner's or Renter's Insurance"
                        }}
                        options={yesNoOptions}
                        name="home.hasHomeInsurance"
                        control={form.control}
                    />

                    <FormYesNoInput
                        name="homeOwner.appliedFEMA"
                        inputGroupProps={{
                            label: "Has homeowner applied to FEMA?"
                        }}
                        control={form.control}
                    />

                    <FormYesNoInput
                        name="homeOwner.appliedSBA"
                        inputGroupProps={{
                            label: "Has homeowner applied for SBA?"
                        }}
                        control={form.control}
                    />

                    <FormRadioInput
                        inputGroupProps={{
                            label: "FEMA Received",
                        }}
                        options={yesNoRejectedOptions}
                        name="homeOwner.femaStatus"
                        control={form.control}
                    />

                    <FormRadioInput
                        inputGroupProps={{
                            label: "SBA Received?",
                        }}
                        options={yesNoRejectedOptions}
                        name="homeOwner.sbaStatus"
                        control={form.control}
                    />

                    <FormSelectInput
                        inputGroupProps={{
                            label: showRequiredCues ? "Residence Type*" : "Residence Type",
                            highlightWhenEmpty: showRequiredCues,
                        }}
                        options={residenceTypeOptions}
                        name="home.repairabilityInfo.residenceType"
                        control={form.control}
                    />

                    <FormTextareaInput
                        inputGroupProps={{
                            label: "Please share anything you feel comfortable about how the storm has affected your home or daily life. This helps us connect you with resources and support. No details are required.",
                            wrapperClassName: "three-cols",
                        }}
                        rows={4}
                        name="homeOwner.ownerStory"
                        control={form.control}
                    />

                    <FormTextareaInput
                        inputGroupProps={{
                            label: "Prayer Requests",
                            wrapperClassName: "three-cols",
                        }}
                        rows={4}
                        name="homeOwner.prayerRequest"
                        control={form.control}
                    />
                </HomeDetailsWrapper>
                </form>
                <StickySaveBar 
                    isVisible={isFormInitialized && ((isDirty && Object.keys(dirtyFields).length > 0) || showSuccessBar)}
                    isSuccess={showSuccessBar}
                    isSubmitting={isSubmitting}
                    onSave={handleStickySave}
                />
            </DirtyFieldsProvider>
        </FormProvider>
    )
}

export default HomeownerProfileForm

